import React, { useState } from 'react';
import PropTypes from 'prop-types';
import BsModal from 'react-bootstrap/Modal';
import { toast } from '../../toasts';
import { FaDownload, FaFileAlt, FaCheckCircle } from 'react-icons/fa';
import { getError } from '../../utils';
import Button from './Button';
import { saveBlob } from './saveBlob';

const FORMATS = [
	{ value: 'pdf', label: 'PDF', enabled: false },
	{ value: 'xlsx', label: 'XLSX', enabled: true },
	{ value: 'csv', label: 'CSV', enabled: false },
];

/**
 * Export dialog for entity lists (idle -> preparing -> ready). `fields`
 * drives a per-field checkbox picker (a field flagged `locked` is always
 * checked and can't be unchecked — used for the row's identifying column).
 * XLSX is currently the only working format — PDF/CSV render per the design
 * spec but stay disabled until there's a real export path for them.
 */
function ExportModal({ show, onClose, count, fields, subtitle, onExport }) {
	const [state, setState] = useState('idle');
	const [checked, setChecked] = useState(() => fields.map((f) => f.key));
	const [format, setFormat] = useState('xlsx');
	const [result, setResult] = useState(null);

	const reset = () => {
		setState('idle');
		setChecked(fields.map((f) => f.key));
		setFormat('xlsx');
		setResult(null);
	};

	// Reset happens in onExited, once the close transition finishes — resetting
	// here instead would swap the body back to the idle field-picker while the
	// modal is still visibly fading out.
	const handleClose = () => {
		onClose();
	};

	const toggleField = (key, isChecked) => {
		setChecked((prev) => (isChecked ? [...prev, key] : prev.filter((k) => k !== key)));
	};

	const allChecked = checked.length === fields.length;
	const toggleAll = () => setChecked(allChecked ? fields.filter((f) => f.locked).map((f) => f.key) : fields.map((f) => f.key));

	const run = async () => {
		setState('preparing');
		try {
			const exported = await onExport(checked, format);
			setResult(exported);
			setState('ready');
		} catch (err) {
			toast.error(getError(err));
			setState('idle');
		}
	};

	const handleDownload = () => {
		if (result) saveBlob(result.blob, result.filename);
	};

	return (
		<BsModal
			show={show}
			onHide={handleClose}
			centered
			onExited={reset}
			backdrop={state === 'preparing' ? 'static' : true}
			keyboard={state !== 'preparing'}
			dialogClassName='tw:max-w-[520px]'
			contentClassName='entity-list-page el-modal tw:rounded-[14px] tw:border-0 tw:overflow-hidden tw:shadow-overlay tw:font-sans tw:text-[14px]/[1.45] tw:text-ink-0'
		>
			<BsModal.Header
				closeButton={state !== 'preparing'}
				className='tw:items-start tw:border-b tw:border-border tw:px-[22px] tw:pt-[20px] tw:pb-[14px]'
			>
				<div className='tw:flex tw:items-start tw:gap-3'>
					<span className='tw:mt-0.5 tw:flex tw:h-[38px] tw:w-[38px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[9px] tw:bg-accent-soft tw:text-accent'>
						<FaDownload />
					</span>
					<div>
						<BsModal.Title className='tw:text-[17px]/[1.3] tw:font-bold tw:tracking-[-0.01em] tw:text-ink-0'>
							Exportar
						</BsModal.Title>
						<p className='tw:mb-0 tw:mt-1 tw:text-[12.5px] tw:text-ink-2'>{subtitle}</p>
					</div>
				</div>
			</BsModal.Header>

			{state === 'idle' && (
				<>
					<BsModal.Body className='tw:flex tw:flex-col tw:gap-3.5 tw:px-[22px] tw:py-[18px]'>
						<div>
							<div className='tw:mb-2 tw:flex tw:items-center tw:justify-between'>
								<span className='tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>
									Campos
								</span>
								<Button variant='ghost' size='sm' onClick={toggleAll} className='tw:border-0'>
									{allChecked ? 'Deseleccionar todo' : 'Seleccionar todo'}
								</Button>
							</div>
							<div className='tw:flex tw:flex-col tw:gap-1.5'>
								{fields.map((field) => {
									const isChecked = checked.includes(field.key);
									return (
										<label
											key={field.key}
											className={`tw:flex tw:items-center tw:gap-3 tw:rounded-[8px] tw:border tw:px-3 tw:py-2.5 tw:transition-[border-color,background] tw:duration-100 ${
												field.locked ? 'tw:cursor-not-allowed' : 'tw:cursor-pointer'
											} ${
												isChecked
													? 'tw:border-accent tw:bg-accent-soft'
													: 'tw:border-border tw:hover:border-border-strong'
											}`}
										>
											<input
												type='checkbox'
												checked={isChecked}
												disabled={field.locked}
												onChange={(e) => toggleField(field.key, e.target.checked)}
												className='tw:h-4 tw:w-4 tw:accent-accent'
											/>
											<span
												className={`tw:flex tw:h-[30px] tw:w-[30px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[7px] ${
													isChecked ? 'tw:bg-accent tw:text-white' : 'tw:bg-surface-3 tw:text-ink-1'
												}`}
											>
												<FaFileAlt />
											</span>
											<div className='tw:flex-1'>
												<div className='tw:text-[14px] tw:font-medium tw:text-ink-0'>{field.label}</div>
												{field.sub && <div className='tw:text-[11.5px] tw:text-ink-3'>{field.sub}</div>}
											</div>
										</label>
									);
								})}
							</div>
						</div>

						<div>
							<div className='tw:mb-2 tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>
								Formato
							</div>
							<div className='tw:inline-flex tw:overflow-hidden tw:rounded-[7px] tw:border tw:border-border'>
								{FORMATS.map((f) => (
									<button
										key={f.value}
										type='button'
										disabled={!f.enabled}
										onClick={() => f.enabled && setFormat(f.value)}
										className={`tw:flex-1 tw:border-0 tw:px-3 tw:py-1.5 tw:text-[12.5px] tw:font-medium ${
											format === f.value
												? 'tw:bg-accent tw:text-white'
												: f.enabled
													? 'tw:bg-transparent tw:text-ink-2'
													: 'tw:cursor-not-allowed tw:bg-transparent tw:text-ink-4'
										}`}
									>
										{f.label}
									</button>
								))}
							</div>
						</div>

						<div className='tw:rounded-[7px] tw:border tw:border-border tw:bg-surface-2 tw:px-3 tw:py-2.5 tw:text-[12px] tw:text-ink-3'>
							Se exportarán {checked.length} de {fields.length} campos de {count} registros en formato{' '}
							{format.toUpperCase()}.
						</div>
					</BsModal.Body>
					<BsModal.Footer className='tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px] tw:gap-2'>
						<Button variant='default' onClick={handleClose}>
							Cancelar
						</Button>
						<Button
							variant='primary'
							icon={<FaDownload />}
							disabled={checked.length === 0}
							className={checked.length === 0 ? 'tw:opacity-50' : ''}
							onClick={run}
						>
							Exportar
						</Button>
					</BsModal.Footer>
				</>
			)}

			{state === 'preparing' && (
				<BsModal.Body className='tw:flex tw:flex-col tw:items-center tw:gap-1 tw:px-[22px] tw:py-12 tw:text-center'>
					<div className='tw:mb-3 tw:h-14 tw:w-14 tw:animate-spin tw:rounded-full tw:border-4 tw:border-surface-3 tw:border-t-accent' />
					<div className='tw:font-semibold tw:text-ink-0'>Preparando archivo…</div>
					<div className='tw:text-[12.5px] tw:text-ink-3'>Compilando datos</div>
				</BsModal.Body>
			)}

			{state === 'ready' && (
				<>
					<BsModal.Body className='tw:flex tw:flex-col tw:items-center tw:gap-1 tw:px-[22px] tw:py-9 tw:text-center'>
						<div className='tw:mb-3 tw:flex tw:h-14 tw:w-14 tw:items-center tw:justify-center tw:rounded-full tw:bg-ok-soft tw:text-ok tw:text-[22px]'>
							<FaCheckCircle />
						</div>
						<div className='tw:text-[15px] tw:font-semibold tw:text-ink-0'>Archivo listo</div>
						<div className='tw:text-[12.5px] tw:text-ink-3'>{result?.filename}</div>
					</BsModal.Body>
					<BsModal.Footer className='tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px] tw:gap-2'>
						<Button variant='default' onClick={handleClose}>
							Cerrar
						</Button>
						<Button variant='primary' icon={<FaDownload />} onClick={handleDownload}>
							Descargar
						</Button>
					</BsModal.Footer>
				</>
			)}
		</BsModal>
	);
}

ExportModal.propTypes = {
	show: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	count: PropTypes.number,
	fields: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			sub: PropTypes.string,
			locked: PropTypes.bool,
		}),
	).isRequired,
	subtitle: PropTypes.string,
	onExport: PropTypes.func.isRequired,
};

export default ExportModal;
